import React, { ComponentType, useEffect } from 'react';
import { AppPageComponent } from '@/types/app';
import withOnlyUnauthorizedUsers from '@/utilities/withOnlyUnauthorizedUsers';
import { useAppDispatch } from '@/components/AppState';
import { comesFromVhp, CZEN_BASE_PATH, ReferrerCategory } from '@/constants';
import { useRouter } from 'next/router';
import { useRetrieveVhpMfeSharedData } from '@/components/hooks/useVhpMfeSharedData';

/**
 * This is a general code for all enrollment starting points.
 */
export default function withFlowStartPoint<P extends object>(
  Component: ComponentType<P>
): ComponentType<P> {
  const WithOnlyUnauthorizedUsersComponent = withOnlyUnauthorizedUsers(Component, CZEN_BASE_PATH);

  function FlowStartPoint(props: P) {
    const dispatch = useAppDispatch();
    const router = useRouter();
    const { data, isFetching: isFetchingVhpData } = useRetrieveVhpMfeSharedData();

    useEffect(() => {
      dispatch({ type: 'setIsEnrollmentRebrand', isEnrollmentRebrand: false });
    }, []);

    useEffect(() => {
      const { fromSeoPage, fromSemPage } = router.query;
      if (!isFetchingVhpData && data?.comeFrom === comesFromVhp) {
        dispatch({ type: 'setReferrerCategory', referrerCategory: ReferrerCategory.VHP });
        return;
      }
      if (fromSeoPage === 'true') {
        dispatch({ type: 'setReferrerCategory', referrerCategory: ReferrerCategory.SEO });
        return;
      }
      if (fromSemPage === 'true') {
        dispatch({ type: 'setReferrerCategory', referrerCategory: ReferrerCategory.SEM });
      }
    }, [isFetchingVhpData]);

    return <WithOnlyUnauthorizedUsersComponent {...props} />;
  }

  FlowStartPoint.displayName = `withFlowStartPoint(${Component.displayName || Component.name})`;
  FlowStartPoint.disableScreenViewed = (Component as AppPageComponent).disableScreenViewed;
  FlowStartPoint.Layout = (Component as AppPageComponent).Layout;
  FlowStartPoint.Header = (Component as AppPageComponent).Header;
  FlowStartPoint.Footer = (Component as AppPageComponent).Footer;
  FlowStartPoint.removeBGWrapperClass = (Component as AppPageComponent).removeBGWrapperClass;

  return FlowStartPoint;
}
